import React, { useRef, useEffect } from 'react';
import { useBuildEngagement } from '../hooks/useBuildEngagement';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatCompactCount } from '../utils/engagementMeta';

interface LikeBuildButtonProps {
  vehicleId: number;
  initialLiked?: boolean;
  initialLikeCount?: number;
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
  className?: string;
  onLikeChange?: (liked: boolean, likeCount: number) => void;
}

const sizeConfig = {
  sm: { icon: 'w-4 h-4', text: 'text-xs', gap: 'gap-1' },
  md: { icon: 'w-5 h-5', text: 'text-sm', gap: 'gap-1.5' },
  lg: { icon: 'w-6 h-6', text: 'text-base', gap: 'gap-2' },
};

/** Public appreciation (heart). Distinct from Favorite (star / save to revisit). */
export const LikeBuildButton: React.FC<LikeBuildButtonProps> = ({
  vehicleId,
  initialLiked = false,
  initialLikeCount = 0,
  size = 'md',
  showCount = true,
  className = '',
  onLikeChange,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const account = isAuthenticated ? `user:${user?.id ?? 'session'}` : 'guest';
  const {
    selected: liked,
    count: likeCount,
    pending,
    toggle,
  } = useBuildEngagement(
    account,
    vehicleId,
    'like',
    isAuthenticated && initialLiked,
    initialLikeCount
  );
  const currentIdentity = useRef('');
  const identity = `${account}:${vehicleId}`;
  currentIdentity.current = identity;
  useEffect(() => {
    currentIdentity.current = identity;
    return () => {
      currentIdentity.current = '';
    };
  }, [identity]);

  const cfg = sizeConfig[size];

  const redirectToLogin = () => {
    navigate('/login', { state: { from: location.pathname + location.search } });
  };

  const handleToggle = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;

    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }

    try {
      const result = await toggle(async (next) => {
        const response = await apiService.toggleVehicleLike(vehicleId, next);
        return { selected: response.liked, count: response.like_count };
      });
      if (!result || currentIdentity.current !== identity) return;
      // Commit list membership only after success; failed saves must not remove cards.
      onLikeChange?.(result.selected, result.count);
    } catch (err: unknown) {
      if (currentIdentity.current !== identity) return;
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('authentication')) {
        redirectToLogin();
      } else {
        showToast(message || 'Couldn’t update like. Try again.', 'error');
      }
    }
  };

  const label = liked ? 'Unlike' : 'Like';

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={pending}
      aria-pressed={liked}
      aria-label={label}
      title={label}
      className={[
        'relative inline-flex items-center justify-center min-h-[44px] min-w-[44px]',
        'rounded-lg border border-white/[0.10] bg-[#141414]/70 backdrop-blur-sm',
        'text-gray-200 hover:bg-white/[0.06] hover:text-white',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900',
        'transition-colors select-none',
        pending ? 'opacity-70 cursor-wait' : 'cursor-pointer',
        liked ? 'border-red-500/40 text-red-300' : '',
        cfg.gap,
        'px-2.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {liked ? (
        <HeartSolid className={`${cfg.icon} text-red-400`} aria-hidden />
      ) : (
        <HeartOutline className={cfg.icon} aria-hidden />
      )}
      {showCount ? (
        <span className={`${cfg.text} font-medium tabular-nums`}>
          {formatCompactCount(likeCount)}
        </span>
      ) : null}
    </button>
  );
};

export default LikeBuildButton;
